import { useMemo, useState } from 'react';
import {
  autenticarConta,
  criarConta,
  existeConta,
  listarNicknames,
  migrarHistoricoLegado
} from '../lib/contas';
import { listarPartidasArquivadas } from '../lib/storage';
import type { Preferencias } from '../types';
import { Icone } from './Icone';

interface Props {
  preferencias: Preferencias;
  /** Nickname pré-selecionado (ex.: chip na home). */
  nicknameInicial?: string;
  aoEntrar: (preferencias: Preferencias) => void;
  aoRemoverNome?: (nickname: string) => void;
}

type ModoForm = 'entrar' | 'cadastrar';

export function EntradaNome({ preferencias, nicknameInicial = '', aoEntrar, aoRemoverNome }: Props) {
  const [tickLista, setTickLista] = useState(0);
  const nicknames = useMemo(() => {
    const dasContas = listarNicknames();
    const legado = preferencias.nomesSalvos ?? [];
    return [...new Set([...dasContas, ...legado.map((n) => n.trim()).filter(Boolean)])].sort((a, b) =>
      a.localeCompare(b, 'pt')
    );
    // tickLista força refresh após remover
  }, [preferencias.nomesSalvos, tickLista]);

  const [modo, setModo] = useState<ModoForm>(() =>
    nicknameInicial && existeConta(nicknameInicial) ? 'entrar' : nicknames.length > 0 ? 'entrar' : 'cadastrar'
  );
  const [nickname, setNickname] = useState(nicknameInicial || preferencias.nomeJogador?.trim() || '');
  const [senha, setSenha] = useState('');
  const [senha2, setSenha2] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [pendente, setPendente] = useState(false);

  async function submeter() {
    const nome = nickname.trim();
    setAviso(null);
    setPendente(true);
    try {
      if (modo === 'cadastrar') {
        if (senha !== senha2) {
          setAviso('As senhas não coincidem.');
          return;
        }
        const legado = listarPartidasArquivadas().filter(
          (p) =>
            p.nomeJogador.toLowerCase() === nome.toLowerCase() ||
            p.nomeBrancas.toLowerCase() === nome.toLowerCase() ||
            p.nomePretas.toLowerCase() === nome.toLowerCase()
        );
        const resultado = await criarConta(nome, senha, legado);
        if (!resultado.ok) {
          setAviso(resultado.erro);
          return;
        }
        aoEntrar(resultado.preferencias);
        return;
      }

      const resultado = await autenticarConta(nome, senha);
      if (!resultado.ok) {
        setAviso(resultado.erro);
        return;
      }
      if (resultado.conta.historico.length === 0) {
        const legado = listarPartidasArquivadas().filter(
          (p) =>
            p.nomeJogador.toLowerCase() === nome.toLowerCase() ||
            p.nomeBrancas.toLowerCase() === nome.toLowerCase() ||
            p.nomePretas.toLowerCase() === nome.toLowerCase()
        );
        if (legado.length) migrarHistoricoLegado(nome, legado);
      }
      aoEntrar(resultado.preferencias);
    } finally {
      setPendente(false);
    }
  }

  function removerChip(n: string) {
    aoRemoverNome?.(n);
    if (nickname.trim().toLowerCase() === n.trim().toLowerCase()) {
      setNickname('');
      setSenha('');
      setSenha2('');
    }
    setTickLista((t) => t + 1);
    setAviso(null);
  }

  return (
    <div className="sobreposicao entrada-nome-sobreposicao">
      <section className="modal-entrada-nome" role="dialog" aria-modal="true" aria-labelledby="titulo-entrada-nome">
        <span className="selo-modal">
          <Icone nome="pessoas" />
        </span>
        <small>CONTA DO JOGADOR</small>
        <h2 id="titulo-entrada-nome">{modo === 'entrar' ? 'Entrar no salão' : 'Criar cadastro'}</h2>
        <p>
          Nickname + senha simples, só neste aparelho. Depois do primeiro login, este navegador te reconhece e entra
          sozinho. Preferências e histórico ficam na sua conta.
        </p>

        <div className="seletor-segmentado dois abas-conta" role="tablist" aria-label="Modo de acesso">
          <button type="button" role="tab" aria-selected={modo === 'entrar'} className={modo === 'entrar' ? 'selecionado' : ''} onClick={() => { setModo('entrar'); setAviso(null); }}>
            Entrar
          </button>
          <button type="button" role="tab" aria-selected={modo === 'cadastrar'} className={modo === 'cadastrar' ? 'selecionado' : ''} onClick={() => { setModo('cadastrar'); setAviso(null); }}>
            Cadastrar
          </button>
        </div>

        {nicknames.length > 0 && (
          <div className="nomes-salvos">
            <small>Contas neste aparelho · clique no × para remover</small>
            <div className="chips-nomes">
              {nicknames.map((n) => (
                <span key={n} className={nickname === n ? 'chip-nome-grupo ativo' : 'chip-nome-grupo'}>
                  <button
                    type="button"
                    className="chip-nome"
                    onClick={() => {
                      setNickname(n);
                      setModo(existeConta(n) ? 'entrar' : 'cadastrar');
                      setAviso(null);
                    }}
                  >
                    {n}
                  </button>
                  {aoRemoverNome && (
                    <button
                      type="button"
                      className="chip-nome-remover"
                      aria-label={`Remover ${n} deste aparelho`}
                      title="Remover deste aparelho"
                      onClick={() => removerChip(n)}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        <label className="campo-regio">
          <span>Nickname</span>
          <input
            value={nickname}
            maxLength={40}
            autoFocus
            autoComplete="username"
            placeholder="Ex.: Guilherme"
            onChange={(e) => {
              setNickname(e.target.value);
              setAviso(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submeter();
            }}
          />
        </label>

        <label className="campo-regio">
          <span>Senha</span>
          <input
            type="password"
            value={senha}
            maxLength={64}
            autoComplete={modo === 'entrar' ? 'current-password' : 'new-password'}
            placeholder={modo === 'cadastrar' ? 'Mínimo 4 caracteres' : 'Sua senha'}
            onChange={(e) => {
              setSenha(e.target.value);
              setAviso(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submeter();
            }}
          />
        </label>

        {modo === 'cadastrar' && (
          <label className="campo-regio">
            <span>Confirmar senha</span>
            <input
              type="password"
              value={senha2}
              maxLength={64}
              autoComplete="new-password"
              placeholder="Repita a senha"
              onChange={(e) => {
                setSenha2(e.target.value);
                setAviso(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submeter();
              }}
            />
          </label>
        )}

        {aviso && <p className="aviso-entrada-nome" role="alert">{aviso}</p>}

        <div className="acoes-resultado" style={{ marginTop: '1rem' }}>
          <button type="button" className="botao-principal" disabled={pendente} onClick={() => void submeter()}>
            {pendente ? 'Aguarde…' : modo === 'entrar' ? 'Entrar' : 'Criar conta e entrar'}
          </button>
        </div>
        <p className="dica-conta">
          A senha não sai deste navegador. Use o × ao lado do nome para apagar a conta deste aparelho.
        </p>
      </section>
    </div>
  );
}
